/**
 * Sales: offerings and lots, readiness, catalogs and data freezes, buyer inquiries, reservations,
 * agreements (private treaty and auction results), delivery, registration transfer, buyer record
 * packets, and purchases and service requests. Interest, a hold, an agreement, payment and delivery
 * stay separate records; sales never moves cattle or money by itself.
 */
import { defineEvent, defineFacet, defineModule, f, fieldLabels, msg, type Badge, type Fact, type FacetEvent, type Fields, type Formatters, type Money, type SubjectRef, type SubjectSpec } from "../../contract";
import {
  LOT_KIND_LABELS, LOT_STATUS_LABELS, OFFERING_KIND_LABELS, PACKET_CATEGORY_LABELS, PRICE_BASIS_LABELS,
} from "./shared/types";
import { ALL_LABELS } from "./shared/labels";

// ---------------------------------------------------------------- shared pieces

const buyer: SubjectSpec = { role: "buyer", kind: "contact", label: "Buyer", required: true };
const offering: SubjectSpec = { role: "offering", kind: "job", label: "Sale", help: "The sale offering this belongs to" };
const animals: SubjectSpec = { role: "animal", kind: "animal", label: "Animals", many: true };

const lotsField = (o: { required?: boolean; level?: "core" | "more" } = {}) =>
  f.list("Lots", f.text("Lot", { max: 20 }), { itemLabel: "Lot", max: 200, agent: "Lot numbers exactly as said, e.g. 12 or 12A. Lot numbers belong to the sale and are not ear tags.", ...o });

const subjectOf = (subjects: SubjectRef[], role: string) => subjects.find((s) => s.role === role);
const labelOf = (s: SubjectRef | undefined, x: Formatters) => (s ? s.label ?? x.label(s.id) : "");
const animalList = (subjects: SubjectRef[], x: Formatters) => {
  const list = subjects.filter((s) => s.role === "animal");
  return list.length > 4 ? x.count(list.length, "{{count}} animal", "{{count}} animals") : x.list(list.map((s) => labelOf(s, x)));
};
const lotsText = (lots: string[] | undefined, x: Formatters) =>
  !lots?.length ? "" : lots.length === 1 ? x.t("lot {{lot}}", { lot: lots[0] }) : x.t("lots {{lots}}", { lots: lots.join(", ") });
const whatText = (data: { lots?: string[] }, subjects: SubjectRef[], x: Formatters) => lotsText(data.lots, x) || animalList(subjects, x);

function priceText(price: Money | undefined, basis: string | undefined, x: Formatters): string {
  if (!price) return "";
  const amount = x.money(price);
  if (basis === "per_head") return x.t("{{amount}} a head", { amount });
  if (basis === "per_cwt") return x.t("{{amount}} a hundredweight", { amount });
  if (basis === "per_lb") return x.t("{{amount}} a pound", { amount });
  if (basis === "per_lot") return x.t("{{amount}} a lot", { amount });
  return amount;
}

// ---------------------------------------------------------------- events

export interface ListingData { lot: string; status: "listed" | "withdrawn" | "no_sale"; note?: string }
const listing = defineEvent<ListingData>({
  type: "sales.listing",
  label: "Listed for sale",
  description: "Animals put into a sale as a lot, pulled from it, or a lot that passed without selling.",
  icon: "tag",
  tone: "sale",
  permission: "manage",
  subjects: [{ ...animals, required: true }, { ...offering, required: true }],
  fields: {
    lot: f.text("Lot", { required: true, max: 20, agent: "Lot number exactly as said. Lot numbers belong to the sale and are not ear tags." }),
    status: f.enum("Listing change", { listed: "Listed", withdrawn: "Withdrawn", no_sale: "No sale" }, {
      required: true, agent: "listed when putting animals in a sale; withdrawn when pulling them out; no_sale when the lot passed without selling",
    }),
    note: f.text("Reason", { level: "more" }),
  },
  summary: (e, x) => {
    const values = { lot: e.data.lot, animals: animalList(e.subjects, x), sale: labelOf(subjectOf(e.subjects, "offering"), x) };
    if (e.data.status === "withdrawn") return x.t("Pulled {{animals}} from lot {{lot}}, {{sale}}", values);
    if (e.data.status === "no_sale") return x.t("No sale: lot {{lot}} ({{animals}}), {{sale}}", values);
    return x.t("Lot {{lot}} in {{sale}}: {{animals}}", values);
  },
  agent: {
    when: "Putting animals into a sale offering as a lot, pulling them out, or recording that a lot passed without selling.",
    avoid: "Not a sale: use sales.agreement when a buyer agreed to buy. The offering must exist; when there is none, propose a sales.offering job first and refer to it.",
    examples: ["put B20 in the spring sale as lot 12", "pull 718 from the bull sale", "lot 14 didn't sell"],
  },
});

export interface InquiryData {
  lots?: string[]; follow_up_on?: string; follow_up?: string; wants?: string; budget?: Money;
  contact_by?: "call" | "text" | "email" | "visit"; language?: "en" | "es"; notes?: string; status?: "open" | "closed";
}
const inquiry = defineEvent<InquiryData>({
  type: "sales.inquiry",
  label: "Buyer inquiry",
  description: "A buyer asked about cattle for sale. Interest only: not a hold and not a sale.",
  icon: "contact",
  tone: "sale",
  subjects: [buyer, offering, { ...animals, label: "Animals of interest" }],
  fields: {
    lots: lotsField(),
    follow_up_on: f.date("Follow up on", { agent: "The date to call or write back, when said ('call him Friday')" }),
    follow_up: f.text("Follow-up", { placeholder: "Call back, send exam records…", agent: "What to do next, in the speaker's words: 'call him', 'send the BSE results'" }),
    wants: f.text("Looking for", { level: "more", agent: "What the buyer is looking for, only when said: 'calving-ease bulls for heifers'" }),
    budget: f.money("Budget", { level: "more", agent: "Only when the buyer volunteered a budget" }),
    contact_by: f.enum("Prefers", { call: "Phone call", text: "Text", email: "Email", visit: "Visit in person" }, { level: "more" }),
    language: f.enum("Language", { en: "English", es: "Spanish" }, { level: "more" }),
    notes: f.longText("Notes", { level: "more", agent: "Keep the speaker's words" }),
    status: f.enum("Inquiry", { open: "Open", closed: "Closed" }, { level: "more", agent: "Leave out unless the inquiry is finished" }),
  },
  summary: (e, x) => {
    const who = labelOf(subjectOf(e.subjects, "buyer"), x);
    const what = whatText(e.data, e.subjects, x);
    const base = what ? x.t("{{buyer}} asked about {{what}}", { buyer: who, what }) : x.t("{{buyer}} asked about cattle for sale", { buyer: who });
    const follow = e.data.status === "closed" ? x.t("closed") : e.data.follow_up_on ? x.t("follow up {{date}}", { date: x.date(e.data.follow_up_on) }) : "";
    return follow ? `${base}; ${follow}` : base;
  },
  agent: {
    when: "A buyer asked about cattle for sale, wants certain lots, or asked to be called back. Interest only.",
    avoid: "If the buyer agreed to buy, use sales.agreement. If the seller is holding a lot for them, use sales.reservation.",
    examples: ["Bob Jones wants lots 12 and 19, call him Friday", "Elena called about the red bull, send her the BSE results"],
  },
  effects: {
    followUps: (d, _on, subjects) => {
      if (d.status === "closed" || !d.follow_up_on) return [];
      const buyerId = subjectOf(subjects, "buyer")?.id;
      const title = d.follow_up ? msg("Buyer follow-up: {{what}}", { what: d.follow_up })
        : d.lots?.length ? msg("Follow up with buyer about lots {{lots}}", { lots: d.lots.join(", ") }) : msg("Follow up with buyer");
      return [{ key: "follow_up", title, due_at: d.follow_up_on.slice(0, 10), ...(buyerId ? { subject: { kind: "contact" as const, id: buyerId } } : {}) }];
    },
  },
});

export interface OfferData { lots?: string[]; amount?: Money; price_basis?: "total" | "per_head" | "per_cwt" | "per_lb"; status: "open" | "declined" | "withdrawn"; expires?: string; conditions?: string }
const offer = defineEvent<OfferData>({
  type: "sales.offer",
  label: "Buyer offer",
  description: "A buyer offered a price. It isn't a sale until the seller accepts and records the agreement.",
  icon: "coins",
  tone: "sale",
  subjects: [buyer, { ...offering, required: true }, animals],
  fields: {
    lots: lotsField(),
    amount: f.money("Offer", { agent: "The amount the buyer offered, only when said" }),
    price_basis: f.enum("Offer is", { total: "Total", per_head: "Per head", per_cwt: "Per hundredweight", per_lb: "Per pound" }, {
      agent: "total when one amount covers everything offered here; per_head, per_cwt or per_lb only when said",
    }),
    status: f.enum("Offer status", { open: "Waiting on you", declined: "Turned down", withdrawn: "Buyer took it back" }, { required: true, agent: "open for a new offer" }),
    expires: f.date("Good until", { level: "more", agent: "Only when the buyer gave a deadline" }),
    conditions: f.longText("Conditions", { level: "more", agent: "e.g. if he passes his breeding soundness exam" }),
  },
  validate: (d) => (d.amount && !d.price_basis ? [{ path: "price_basis", message: "Say whether the price is the total, per head or by weight" }] : []),
  summary: (e, x) => {
    const values = { what: whatText(e.data, e.subjects, x), buyer: labelOf(subjectOf(e.subjects, "buyer"), x), amount: priceText(e.data.amount, e.data.price_basis, x) };
    if (e.data.status === "declined") return x.t("Turned down {{buyer}}'s offer for {{what}}", values);
    if (e.data.status === "withdrawn") return x.t("{{buyer}} took back the offer for {{what}}", values);
    return values.amount ? x.t("{{buyer}} offered {{amount}} for {{what}}", values) : x.t("{{buyer}} made an offer for {{what}}", values);
  },
  agent: {
    when: "A buyer offered a price the seller hasn't accepted yet, or an offer was turned down or taken back.",
    avoid: "When the seller accepted, record the sale itself (sales.agreement). Asking about a lot without a price is sales.inquiry.",
    examples: ["Bob offered $6,000 for lot 12, good till Friday", "turned down the $4,500 offer on the red bull"],
  },
});

export interface ReservationData { lots?: string[]; status: "held" | "released"; until?: string; deposit?: Money; note?: string }
const reservation = defineEvent<ReservationData>({
  type: "sales.reservation",
  label: "Reservation",
  description: "The seller agreed to hold lots for a buyer, or let a hold go.",
  icon: "clock",
  tone: "sale",
  permission: "manage",
  review: "always",
  subjects: [buyer, { ...offering, required: true }, animals],
  fields: {
    lots: lotsField(),
    status: f.enum("Hold", { held: "Held for buyer", released: "Released" }, { required: true, agent: "held when the seller agreed to hold; released when the hold ended without a sale" }),
    until: f.date("Held until", { agent: "Only when an end date was said" }),
    deposit: f.money("Deposit", { level: "more" }),
    note: f.text("Terms", { level: "more" }),
  },
  summary: (e, x) => {
    const values = { what: whatText(e.data, e.subjects, x), buyer: labelOf(subjectOf(e.subjects, "buyer"), x) };
    if (e.data.status === "released") return x.t("Released hold on {{what}} for {{buyer}}", values);
    return e.data.until ? x.t("Holding {{what}} for {{buyer}} until {{date}}", { ...values, date: x.date(e.data.until) }) : x.t("Holding {{what}} for {{buyer}}", values);
  },
  agent: {
    when: "The seller agreed to hold one or more lots for a specific buyer, or released such a hold.",
    avoid: "Not interest (sales.inquiry) and not a sale (sales.agreement).",
    examples: ["hold lot 12 for Bob Jones until the sale", "Bob doesn't want lot 12 anymore, let it go"],
  },
});

export interface AgreementData {
  lots?: string[]; price?: Money; price_basis?: "total" | "per_head" | "per_cwt" | "per_lb"; delivery_by?: string;
  channel?: "private_treaty" | "auction" | "online" | "sale_barn"; status?: "agreed" | "cancelled"; deposit?: Money;
  guarantee?: string; retained?: string; conditions?: string; notes?: string;
}
const agreement = defineEvent<AgreementData>({
  type: "sales.agreement",
  label: "Sale agreement",
  description: "A buyer agreed to buy: private treaty terms or an auction result. Payment, delivery and registration transfer are tracked separately.",
  icon: "handshake",
  tone: "sale",
  permission: "manage",
  review: "always",
  subjects: [buyer, { ...offering, required: true }, animals],
  fields: {
    lots: lotsField(),
    price: f.money("Price", { agent: "The agreed price, only when said" }),
    price_basis: f.enum("Price is", { total: "Total", per_head: "Per head", per_cwt: "Per hundredweight", per_lb: "Per pound" }, {
      agent: "total when one amount covers everything sold here; per_head, per_cwt or per_lb only when said",
    }),
    delivery_by: f.date("Delivery", { agent: "Delivery date, when said ('next week' resolves to a date, estimated)" }),
    channel: f.enum("Sold by", { private_treaty: "Private treaty", auction: "Auction", online: "Online or video auction", sale_barn: "Sale barn" }, { level: "more" }),
    status: f.enum("Agreement", { agreed: "Agreed", cancelled: "Cancelled" }, { level: "more", agent: "cancelled only when a buyer backed out of an earlier agreement" }),
    deposit: f.money("Deposit", { level: "more" }),
    guarantee: f.longText("Guarantee", { level: "more", agent: "Breeding or fertility guarantee as said" }),
    retained: f.text("Retained interests", { level: "more", agent: "Rights the seller keeps, e.g. one-third semen revenue, exactly as said. Never turn this into ownership percentages." }),
    conditions: f.longText("Conditions", { level: "more", agent: "e.g. subject to a passing breeding soundness exam" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: (d) => (d.price && !d.price_basis ? [{ path: "price_basis", message: "Say whether the price is the total, per head or by weight" }] : []),
  summary: (e, x) => {
    const values = { what: whatText(e.data, e.subjects, x), buyer: labelOf(subjectOf(e.subjects, "buyer"), x) };
    if (e.data.status === "cancelled") return x.t("Cancelled sale of {{what}} to {{buyer}}", values);
    const parts = [x.t("Sold {{what}} to {{buyer}}", values)];
    const price = priceText(e.data.price, e.data.price_basis, x);
    if (price) parts.push(price);
    if (e.data.delivery_by) parts.push(x.t("delivery {{date}}", { date: x.date(e.data.delivery_by) }));
    return parts.join(", ");
  },
  agent: {
    when: "A buyer agreed to buy cattle: a private treaty deal, an auction or sale-barn result, or an accepted offer. Records the terms only.",
    avoid: "Not for interest (sales.inquiry) or a hold (sales.reservation). Loading the cattle is sales.delivery. Never invent a price or a delivery date.",
    examples: ["sold B20 private treaty to Bob Jones for $6,500, delivery next week", "Maria took lots 4 and 5 for $12,000 total, $2,000 down"],
  },
});

export interface DeliveryData {
  lots?: string[]; head?: number; destination?: string; health_papers?: "done" | "not_needed" | "pending";
  brand_inspection?: boolean; weight?: { value: number; unit: string }; notes?: string;
}
const delivery = defineEvent<DeliveryData>({
  type: "sales.delivery",
  label: "Delivery",
  description: "Sold or consigned cattle actually left on a truck. Records the herd departure too.",
  icon: "truck",
  tone: "sale",
  review: "always",
  subjects: [animals, { ...buyer, required: false, label: "Buyer or sale barn" }, offering, { role: "trucker", kind: "contact", label: "Trucker" }],
  fields: {
    lots: lotsField({ level: "more" }),
    head: f.int("Head", { level: "more", min: 1, help: "For counted lots without tags" }),
    destination: f.text("Delivered to", { agent: "Where they went, when said: the buyer's place, the sale barn" }),
    health_papers: f.enum("Health papers", { done: "Done", not_needed: "Not needed", pending: "Still needed" }, { level: "more" }),
    brand_inspection: f.bool("Brand inspected", { level: "more" }),
    weight: f.quantity("Shipped weight", "weight", { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  validate: (d, subjects) => (!subjects.some((s) => s.role === "animal") && !d.head && !d.lots?.length ? [{ path: "subjects.animal", message: "Say which cattle went" }] : []),
  summary: (e, x) => {
    const who = labelOf(subjectOf(e.subjects, "buyer"), x);
    const what = e.subjects.some((s) => s.role === "animal") ? animalList(e.subjects, x) : e.data.head ? x.count(e.data.head, "{{count}} head", "{{count}} head") : lotsText(e.data.lots, x);
    const base = who ? x.t("Delivered {{what}} to {{buyer}}", { what, buyer: who }) : x.t("Hauled {{what}}", { what });
    const extra = [e.data.destination, labelOf(subjectOf(e.subjects, "trucker"), x) ? x.t("hauled by {{trucker}}", { trucker: labelOf(subjectOf(e.subjects, "trucker"), x) }) : ""].filter(Boolean);
    return [base, ...extra].join(", ");
  },
  agent: {
    when: "Cattle actually left on a truck to a buyer or a sale barn: loaded, hauled, delivered.",
    avoid: "A planned load-out ('load out the 5 cull cows Thursday') is not a delivery yet: propose a sales.offering job (sale barn) with those animals and the load-out date as due, or a task. Not for moves between your own pastures.",
    examples: ["hauled B20 to Bob Jones this morning", "loaded the 5 cull cows for Lampasas, D&R hauled them"],
  },
});

export interface TransferData { status: "started" | "submitted" | "done" | "not_needed"; association_id?: string; reference?: string }
const transfer = defineEvent<TransferData>({
  type: "sales.transfer",
  label: "Registration transfer",
  description: "Registration paperwork moving a sold animal into the buyer's name.",
  icon: "file",
  tone: "sale",
  subjects: [{ ...animals, required: true }, { ...buyer, required: false }],
  fields: {
    status: f.enum("Transfer", { started: "Paperwork started", submitted: "Sent to the association", done: "Transferred", not_needed: "Not needed" }, { required: true }),
    association_id: f.ref("Association", "contact", { level: "more" }),
    reference: f.text("Reference number", { level: "more" }),
  },
  summary: (e, x) => {
    const values = { animals: animalList(e.subjects, x) };
    switch (e.data.status) {
      case "started": return x.t("Registration transfer started: {{animals}}", values);
      case "submitted": return x.t("Registration transfer sent: {{animals}}", values);
      case "done": return x.t("Registration transferred: {{animals}}", values);
      default: return x.t("No registration transfer needed: {{animals}}", values);
    }
  },
  agent: {
    when: "Registration transfer paperwork for a sold animal was started, sent, finished, or isn't needed.",
    examples: ["sent the transfer for B20 to the Red Angus association"],
  },
});

const PACKET_CATEGORIES = PACKET_CATEGORY_LABELS;
export interface HandoffData { packet: string; version?: number; categories?: string[] }
const handoff = defineEvent<HandoffData>({
  type: "sales.handoff",
  label: "Records shared with buyer",
  description: "A frozen packet of this animal's records was shared with its buyer.",
  icon: "packet",
  tone: "sale",
  hidden: true,
  permission: "manage",
  subjects: [{ ...animals, required: true }, { ...buyer, required: false, label: "Sent to" }],
  fields: {
    packet: f.text("Packet", { required: true }),
    version: f.int("Version", { min: 1 }),
    categories: f.multiEnum("Shared", PACKET_CATEGORIES),
  },
  summary: (e, x) => {
    const who = labelOf(subjectOf(e.subjects, "buyer"), x);
    return who ? x.t("Shared records with {{buyer}} (version {{version}})", { buyer: who, version: e.data.version ?? 1 }) : x.t("Shared records (version {{version}})", { version: e.data.version ?? 1 });
  },
});

export interface ReceivedData { source_ranch: string; version?: number; records?: number; source_label?: string }
const received = defineEvent<ReceivedData>({
  type: "sales.received",
  label: "Records received",
  description: "Records for this animal arrived from the ranch that sold it.",
  icon: "inbox",
  tone: "sale",
  hidden: true,
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    source_ranch: f.text("From ranch", { required: true }),
    version: f.int("Version", { min: 1 }),
    records: f.int("Records", { min: 0 }),
    source_label: f.text("Their tag or name"),
  },
  summary: (e, x) => x.t("Received records from {{ranch}} (version {{version}})", { ranch: e.data.source_ranch, version: e.data.version ?? 1 }),
});

export interface ReceivedRecordData { source_ranch: string; source_type: string; source_label: string; summary: string; source_key?: string }
const receivedRecord = defineEvent<ReceivedRecordData>({
  type: "sales.received_record",
  label: "Record from seller",
  description: "A record that came with the animal from another ranch, kept as they wrote it.",
  icon: "inbox",
  tone: "note",
  hidden: true,
  subjects: [{ role: "animal", kind: "animal", label: "Animal", required: true }],
  fields: {
    source_ranch: f.text("From ranch", { required: true }),
    source_type: f.text("Their record type", { required: true }),
    source_label: f.text("Record", { required: true }),
    summary: f.text("What they recorded", { required: true, max: 2000 }),
    source_key: f.text("Their record id", { level: "more" }),
  },
  summary: (e) => e.data.summary,
});

// ---------------------------------------------------------------- purchases and service requests

const supplier: SubjectSpec = { role: "supplier", kind: "contact", label: "Supplier", required: true };
const purchase: SubjectSpec = { role: "purchase", kind: "job", label: "Purchase", required: true };

export interface QuoteData { amount?: Money; basis?: "total" | "per_unit"; what?: string; valid_until?: string; includes?: string }
const quote = defineEvent<QuoteData>({
  type: "sales.quote",
  label: "Quote",
  description: "A supplier or service provider quoted a price.",
  icon: "receipt",
  tone: "money",
  subjects: [supplier, purchase],
  fields: {
    amount: f.money("Amount", { agent: "The quoted price, only when said" }),
    basis: f.enum("Amount is", { total: "Total", per_unit: "Per unit" }),
    what: f.text("For", { agent: "What the quote covers: '40 straws of Sitz Logo', 'haul 20 head to Lampasas'" }),
    valid_until: f.date("Good until", { level: "more" }),
    includes: f.longText("Includes", { level: "more", agent: "Freight, fees, assumptions, exclusions as said" }),
  },
  summary: (e, x) => {
    const who = labelOf(subjectOf(e.subjects, "supplier"), x);
    const amount = e.data.amount ? (e.data.basis === "per_unit" ? x.t("{{amount}} each", { amount: x.money(e.data.amount) }) : x.money(e.data.amount)) : "";
    return [x.t("Quote from {{supplier}}", { supplier: who }), e.data.what, amount].filter(Boolean).join(", ");
  },
  agent: {
    when: "A supplier, hauler or vet quoted a price for something the ranch wants to buy or have done.",
    examples: ["D&R quoted $4.50 a loaded mile to haul the steers"],
  },
});

export interface OrderData { amount?: Money; what?: string; expected_on?: string; notes?: string }
const order = defineEvent<OrderData>({
  type: "sales.order",
  label: "Order placed",
  description: "A purchase or service was approved and ordered.",
  icon: "cart",
  tone: "money",
  permission: "manage",
  review: "always",
  subjects: [supplier, purchase],
  fields: {
    what: f.text("What", { agent: "What was ordered, in the speaker's words" }),
    amount: f.money("Amount", { agent: "The approved amount, only when said" }),
    expected_on: f.date("Expected", { agent: "When it should arrive or happen, when said" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const parts = [x.t("Ordered from {{supplier}}", { supplier: labelOf(subjectOf(e.subjects, "supplier"), x) }), e.data.what ?? ""];
    if (e.data.amount) parts.push(x.money(e.data.amount));
    if (e.data.expected_on) parts.push(x.t("expected {{date}}", { date: x.date(e.data.expected_on) }));
    return parts.filter(Boolean).join(", ");
  },
  agent: {
    when: "Someone approved and placed an order or booked a service: semen, feed, supplies, cattle, hauling.",
    avoid: "A price quote alone is sales.quote. Receiving the goods is sales.receipt.",
    examples: ["ordered 40 straws of Sitz Logo from Brazos, should be here next Tuesday"],
  },
});

export interface ReceiptItem { description: string; lot_id?: string; ordered?: number; received: number; unit?: string }
export interface ReceiptData { what?: string; head?: number; condition?: "good" | "short" | "damaged" | "wrong"; problem?: string; items?: ReceiptItem[]; notes?: string }
const receipt = defineEvent<ReceiptData>({
  type: "sales.receipt",
  label: "Received",
  description: "An order arrived or a service was done, with anything short, damaged or wrong.",
  icon: "check",
  tone: "money",
  review: "always",
  subjects: [{ ...supplier, required: false }, purchase, { ...animals, label: "Cattle received" }],
  fields: {
    what: f.text("What came", { agent: "What arrived, in the speaker's words" }),
    condition: f.enum("Condition", { good: "All good", short: "Short", damaged: "Damaged", wrong: "Wrong item" }, { agent: "Only when said" }),
    problem: f.longText("What was wrong", { agent: "Shortages, damage or substitutions as said" }),
    head: f.int("Head", { level: "more", min: 0 }),
    items: f.list("Products", f.object("Product", {
      description: f.text("Product", { required: true }),
      lot_id: f.ref("Into stock lot", "stock_lot"),
      ordered: f.number("Ordered", { min: 0 }),
      received: f.number("Received", { required: true, min: 0 }),
      unit: f.text("Unit"),
    }), { level: "more", itemLabel: "Product" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const who = labelOf(subjectOf(e.subjects, "supplier"), x);
    const head = e.subjects.filter((s) => s.role === "animal").length || e.data.head;
    const parts = [who ? x.t("Received from {{supplier}}", { supplier: who }) : x.t("Received"), e.data.what ?? "", head ? x.count(head, "{{count}} head", "{{count}} head") : ""];
    if (e.data.condition === "short") parts.push(x.t("short"));
    if (e.data.condition === "damaged") parts.push(x.t("damaged"));
    if (e.data.condition === "wrong") parts.push(x.t("wrong item"));
    return parts.filter(Boolean).join(", ");
  },
  effects: {
    stock: (d) => (d.items ?? []).filter((i) => i.lot_id && i.received > 0).map((i) => ({ lot_id: i.lot_id, delta: i.received, unit: i.unit ?? "each", reason: "receive" as const })),
  },
  agent: {
    when: "Ordered goods arrived, purchased cattle were unloaded, or a booked service was done, including shortages or damage.",
    examples: ["the mineral came, 18 bags, two short", "unloaded the 12 bred heifers from Johnson"],
  },
});

// ---------------------------------------------------------------- facet

export type SalesStatus = "listed" | "reserved" | "sold" | "delivered";
export interface SalesFacet {
  /** Shared keys other modules rely on. */
  offering_id?: string;
  status?: SalesStatus;
  /** The rest is for this module's own screens. */
  lot?: string;
  listed_on?: string;
  last_outcome?: "withdrawn" | "no_sale";
  buyer_id?: string;
  inquiries: number;
  /** Offers waiting on the seller. */
  offers?: number;
  reservation?: { on: string | null; buyer_id: string; until: string | null };
  agreement?: { on: string | null; buyer_id: string; event_id: string; delivery_by: string | null };
  delivery?: { on: string | null; event_id: string; buyer_id: string | null };
  transfer?: { status: string; on: string | null };
  packet?: { id: string; version: number; on: string | null };
  received?: { from: string; version: number; on: string | null };
}

const offeringOf = (e: FacetEvent) => e.subjects.find((s) => s.role === "offering")?.id;
const buyerOf = (e: FacetEvent) => e.subjects.find((s) => s.role === "buyer")?.id;
const day = (e: FacetEvent) => e.occurred_at?.slice(0, 10) ?? null;

export const salesFacet = defineFacet<SalesFacet>({
  id: "sales",
  subject: "animal",
  version: 2,
  eventTypes: ["sales.listing", "sales.inquiry", "sales.offer", "sales.reservation", "sales.agreement", "sales.delivery", "sales.transfer", "sales.handoff", "sales.received"],
  reduce(events) {
    if (!events.length) return null;
    const s: SalesFacet = { inquiries: 0 };
    const open = new Map<string, boolean>();
    let listed = false;
    for (const e of events) {
      const off = offeringOf(e);
      switch (e.type) {
        case "sales.listing": {
          if (e.data.status === "listed") {
            if (off !== s.offering_id) { s.inquiries = 0; delete s.reservation; delete s.agreement; delete s.delivery; delete s.buyer_id; }
            s.offering_id = off; s.lot = e.data.lot; s.listed_on = day(e) ?? undefined; delete s.last_outcome; listed = true;
          } else if (off === s.offering_id) { listed = false; s.last_outcome = e.data.status; }
          break;
        }
        case "sales.inquiry": if (!off || off === s.offering_id) s.inquiries += 1; break;
        case "sales.offer": {
          if (off && s.offering_id && off !== s.offering_id) break;
          const b = buyerOf(e);
          if (!b) break;
          open.set(b, e.data.status === "open");
          break;
        }
        case "sales.reservation": {
          const b = buyerOf(e);
          if (off && s.offering_id && off !== s.offering_id) break;
          if (e.data.status === "held" && b) s.reservation = { on: day(e), buyer_id: b, until: e.data.until ?? null };
          else if (e.data.status === "released" && s.reservation?.buyer_id === b) delete s.reservation;
          break;
        }
        case "sales.agreement": {
          const b = buyerOf(e);
          if (!b) break;
          if (off && !s.offering_id) s.offering_id = off;
          if (off && s.offering_id && off !== s.offering_id) { s.offering_id = off; s.inquiries = 0; delete s.reservation; delete s.delivery; }
          if (e.data.status === "cancelled") { if (s.agreement?.buyer_id === b) { delete s.agreement; delete s.buyer_id; } }
          else { s.agreement = { on: day(e), buyer_id: b, event_id: e.id, delivery_by: e.data.delivery_by ?? null }; s.buyer_id = b; delete s.reservation; open.clear(); }
          break;
        }
        case "sales.delivery": s.delivery = { on: day(e), event_id: e.id, buyer_id: buyerOf(e) ?? null }; if (off) s.offering_id = off; break;
        case "sales.transfer": s.transfer = { status: e.data.status, on: day(e) }; break;
        case "sales.handoff": s.packet = { id: e.data.packet, version: e.data.version ?? 1, on: day(e) }; break;
        case "sales.received": s.received = { from: e.data.source_ranch, version: e.data.version ?? 1, on: day(e) }; break;
      }
    }
    const waiting = [...open.values()].filter(Boolean).length;
    if (waiting && !s.agreement) s.offers = waiting;
    s.status = s.delivery ? "delivered" : s.agreement ? "sold" : s.reservation && listed ? "reserved" : listed ? "listed" : undefined;
    if (!s.status) delete s.status;
    return s;
  },
  badges: (s): Badge[] => {
    if (s.status === "listed") return [{ label: msg("For sale, lot {{lot}}", { lot: s.lot ?? "" }), tone: "sale", icon: "tag" }];
    if (s.status === "reserved") return [{ label: msg("Held for a buyer, lot {{lot}}", { lot: s.lot ?? "" }), tone: "sale", icon: "clock" }];
    if (s.status === "sold") return [{ label: msg("Sold, not delivered"), tone: "sale", icon: "handshake" }];
    return [];
  },
  facts: (s): Fact[] => {
    const out: Fact[] = [];
    if (s.status === "listed" || s.status === "reserved") out.push({ label: "Sale", value: msg("Lot {{lot}}", { lot: s.lot ?? "" }), tone: "sale", icon: "tag" });
    if (s.status === "sold") out.push({ label: "Sale", value: s.agreement?.delivery_by ? msg("Sold, delivery {{date}}", { date: s.agreement.delivery_by }) : msg("Sold, not delivered"), tone: "sale", icon: "handshake" });
    if (s.status === "delivered" && s.delivery?.on) out.push({ label: "Sale", value: msg("Delivered {{date}}", { date: s.delivery.on }), tone: "sale", icon: "truck" });
    if (s.received) out.push({ label: "Records from", value: s.received.from, tone: "sale", icon: "inbox" });
    return out;
  },
  filters: {
    status: { label: "Sale status", type: "enum", options: { listed: "For sale", reserved: "Held for a buyer", sold: "Sold, not delivered", delivered: "Delivered" } },
  },
});

// ---------------------------------------------------------------- jobs and workspaces

export const OFFERING_FIELDS: Fields = {
  kind: f.enum("Kind of sale", OFFERING_KIND_LABELS, { required: true }),
  location: f.text("Where", { placeholder: "At the ranch, Lampasas Livestock Auction…" }),
  manager_id: f.ref("Sale manager or market", "contact", { level: "more" }),
  terms: f.longText("Terms", { level: "more", help: "Shown in the catalog: guarantees, payment, delivery and retained interests that apply to every lot." }),
  price_basis: f.enum("Prices are", { per_head: "Per head", per_lot: "Per lot", per_cwt: "Per hundredweight", per_lb: "Per pound" }, { level: "more" }),
  auction_mode: f.enum("Bidding", { timed: "Timed online close", live: "Live, clerk enters bids" }, { level: "more" }),
  opens_at: f.datetime("Bidding opens", { level: "more" }),
  closes_at: f.datetime("Bidding closes", { level: "more" }),
  increment: f.money("Bid increment", { level: "more" }),
  extension_window_minutes: f.int("Soft close: bids in the last", { level: "more", min: 0, max: 120, suffix: "min" }),
  extension_minutes: f.int("Soft close: extend to", { level: "more", min: 0, max: 120, suffix: "min" }),
  notes: f.longText("Private notes", { level: "more", help: "Never shown in catalogs or buyer records." }),
};

/** Adding a lot from the Lots tab. */
export const LOT_FIELDS: Fields = {
  lot_number: f.text("Lot number", { max: 20, help: "Leave it empty for the next number." }),
  animal_ids: f.refs("Cattle", "animal", { filter: { status: "current" } }),
  head_count: f.int("Head", { level: "more", min: 1, max: 5000, help: "For a counted load without tags" }),
  title: f.text("Lot name", { level: "more", placeholder: "Pair of replacement heifers" }),
  asking: f.money("Asking price", { level: "more" }),
  footnote: f.longText("Footnote", { level: "more", help: "Shown in the catalog under the lot." }),
};

/** Editing a lot's own terms on its page. */
export const LOT_EDIT_FIELDS: Fields = {
  lot_number: f.text("Lot number", { required: true, max: 20 }),
  title: f.text("Lot name", { placeholder: "Pair of replacement heifers" }),
  asking: f.money("Asking price"),
  footnote: f.longText("Footnote", { help: "Shown in the catalog under the lot." }),
  retained: f.text("Retained interests", { level: "more", help: "Rights you keep, as written: one-third semen revenue, first calf back…" }),
  reserve: f.money("Reserve", { level: "more", help: "Private. Never shown to buyers or in the catalog." }),
  price_basis: f.enum("Prices are", { per_head: "Per head", per_lot: "Per lot", per_cwt: "Per hundredweight", per_lb: "Per pound" }, { level: "more" }),
  kind: f.enum("What's in it", LOT_KIND_LABELS, { level: "more" }),
  head_count: f.int("Head", { level: "more", min: 1, max: 5000, help: "For a counted load without tags" }),
};

export const PURCHASE_FIELDS: Fields = {
  kind: f.enum("What kind", { cattle: "Cattle", semen: "Semen or embryos", feed: "Feed, hay or mineral", health: "Animal health supplies", service: "A service: hauling, vet, AI…", other: "Something else" }, { required: true }),
  supplier_id: f.ref("Supplier", "contact"),
  what: f.longText("What you need", { placeholder: "40 straws Sitz Logo for November breeding" }),
  needed_by: f.date("Needed by"),
  budget: f.money("Budget", { level: "more" }),
};

export default defineModule({
  id: "sales",
  label: "Sales",
  description: "Offerings, catalogs, buyers, private treaty, auctions, delivery and buyer handoff.",
  events: [listing, inquiry, offer, reservation, agreement, delivery, transfer, handoff, received, receivedRecord, quote, order, receipt],
  facets: [salesFacet],
  jobs: [
    {
      type: "sales.offering",
      label: "Sale offering",
      description: "Sell cattle: a production sale, private treaty listing, online auction or a sale-barn load. Lots, buyers, delivery and records in one place.",
      icon: "gavel",
      tone: "sale",
      workspace: "sales",
      subjectKind: "animal",
      fields: OFFERING_FIELDS,
      stages: [{ id: "preparing", label: "Preparing" }, { id: "selling", label: "Selling" }, { id: "delivering", label: "Delivering" }],
      agent: { when: "Selling cattle as a named effort: a production bull sale, a private treaty listing, an online or video auction consignment, or a sale-barn load. 'Load out the 5 cull cows Thursday' starts a sale_barn offering with those cows and Thursday as due." },
    },
    {
      type: "sales.purchase",
      label: "Purchase or service request",
      description: "Buy cattle, semen, feed or supplies, or line up a service: quotes, approval, then what actually arrived.",
      icon: "cart",
      tone: "money",
      workspace: "purchases",
      fields: PURCHASE_FIELDS,
      stages: [{ id: "request", label: "Requested" }, { id: "quotes", label: "Getting quotes" }, { id: "ordered", label: "Ordered" }, { id: "received", label: "Received" }],
      agent: { when: "Buying cattle, semen, feed or supplies, or booking a service (hauling, vet work, AI) that needs quotes, approval and receiving." },
    },
  ],
  workspaces: [
    {
      id: "sales",
      label: "Sales",
      description: "Bull sales, private treaty, auctions and sale-barn loads: lots, catalogs, buyers, delivery and buyer records.",
      icon: "gavel",
      group: "sell",
      keywords: ["sell", "sale", "bull sale", "catalog", "buyer", "auction", "private treaty", "lot", "consign", "load out", "delivery", "bid", "reservation", "packet"],
      keywordsEs: ["vender", "venta", "subasta", "remate", "catálogo", "comprador", "toros", "lote", "entrega", "puja", "apartado", "consignar"],
      presets: ["seedstock"],
    },
    {
      id: "purchases",
      label: "Purchases and orders",
      description: "Buy cattle, semen, feed and supplies or book a service, compare quotes, and check what arrived.",
      icon: "cart",
      group: "sell",
      keywords: ["buy", "purchase", "order", "quote", "supplier", "semen order", "hauling", "trucking", "receiving"],
      keywordsEs: ["comprar", "compra", "pedido", "cotización", "proveedor", "flete", "recibir"],
    },
  ],
  attributes: {
    animal: {
      source_ranch: f.text("Received from", { level: "more" }),
      source_packet: f.text("Source packet", { level: "more" }),
      source_key: f.text("Source record", { level: "more" }),
      source_sire: f.text("Sire (as received)", { level: "more" }),
      source_dam: f.text("Dam (as received)", { level: "more" }),
    },
  },
  messages: [
    ...Object.values(LOT_STATUS_LABELS), ...Object.values(OFFERING_KIND_LABELS), ...Object.values(LOT_KIND_LABELS), ...Object.values(PRICE_BASIS_LABELS),
    ...Object.values(PACKET_CATEGORY_LABELS), ...fieldLabels(LOT_FIELDS), ...fieldLabels(LOT_EDIT_FIELDS), ...ALL_LABELS, "Records from",
  ],
});
